import { create } from 'zustand';
import type { UserShootingPreferences } from '@/services/data/shootingPreferences';
import type { UserPlanningPreferences } from '@/types/scheduling';
import type { ConnectedPlatform, MetricoolConnectionStatus } from '@/services/data/socialConnections';

// Connection link prefetch state
export interface ConnectionLinkState {
  status: 'idle' | 'loading' | 'success' | 'error';
  link?: string;
  error?: string;
}

interface OnboardingDataState {
  // Loading state
  isLoading: boolean;
  isLoaded: boolean;
  
  // Data
  shootingPreferences: UserShootingPreferences | null;
  planningPreferences: UserPlanningPreferences | null;
  socialPlatforms: ConnectedPlatform[];
  metricoolConnections: Map<string, MetricoolConnectionStatus>;
  
  // Connection link prefetch (for network connection step)
  connectionLinkState: ConnectionLinkState;
  
  // Actions
  setLoading: (loading: boolean) => void;
  setLoaded: (loaded: boolean) => void;
  setShootingPreferences: (prefs: UserShootingPreferences | null) => void;
  setPlanningPreferences: (prefs: UserPlanningPreferences | null) => void;
  setSocialPlatforms: (platforms: ConnectedPlatform[]) => void;
  setMetricoolConnections: (connections: Map<string, MetricoolConnectionStatus>) => void;
  
  // Connection link actions
  setConnectionLinkLoading: () => void;
  setConnectionLinkSuccess: (link: string) => void;
  setConnectionLinkError: (error: string) => void;
  
  // Partial updates (for optimistic UI)
  updateShootingPreferences: (partial: Partial<UserShootingPreferences>) => void;
  updatePlanningPreferences: (partial: Partial<UserPlanningPreferences>) => void;
  
  // Reset
  reset: () => void;
}

const initialState = {
  isLoading: false,
  isLoaded: false,
  shootingPreferences: null,
  planningPreferences: null,
  socialPlatforms: [],
  metricoolConnections: new Map<string, MetricoolConnectionStatus>(),
  connectionLinkState: { status: 'idle' } as ConnectionLinkState,
};

export const useOnboardingDataStore = create<OnboardingDataState>((set, get) => ({
  ...initialState,
  
  setLoading: (loading) => set({ isLoading: loading }),
  setLoaded: (loaded) => set({ isLoaded: loaded }),
  
  setShootingPreferences: (prefs) => set({ shootingPreferences: prefs }),
  setPlanningPreferences: (prefs) => set({ planningPreferences: prefs }),
  setSocialPlatforms: (platforms) => set({ socialPlatforms: platforms }),
  setMetricoolConnections: (connections) => set({ metricoolConnections: connections }),
  
  // Connection link state management
  setConnectionLinkLoading: () => set({ 
    connectionLinkState: { status: 'loading' } 
  }),
  setConnectionLinkSuccess: (link) => set({ 
    connectionLinkState: { status: 'success', link } 
  }),
  setConnectionLinkError: (error) => set({ 
    connectionLinkState: { status: 'error', error } 
  }),
  
  updateShootingPreferences: (partial) => {
    const current = get().shootingPreferences;
    if (current) {
      set({ shootingPreferences: { ...current, ...partial } });
    } else {
      // Create a minimal object if none exists
      set({ shootingPreferences: partial as UserShootingPreferences });
    }
  },
  
  updatePlanningPreferences: (partial) => {
    const current = get().planningPreferences;
    if (current) {
      set({ planningPreferences: { ...current, ...partial } });
    } else {
      // Create a minimal object if none exists  
      set({ planningPreferences: partial as UserPlanningPreferences });
    }
  },
  
  reset: () => set(initialState),
}));
